import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from '../../lib/zod';
import axios from 'axios';
import { Button, Input } from '@workspace/ui';
import { API_URL } from '../../lib/env';
import { useAuth } from '../../hooks/use-auth';
import { useTenant } from '../../hooks/use-tenant';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const createRegisterSchema = (t: TFunction<'identity'>) => z
  .object({
    email: z.string().email(t('auth.validation.invalidEmail')),
    name: z.string().min(2, t('auth.validation.nameMin')),
    password: z
      .string()
      .min(8, t('auth.validation.passwordMin8'))
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        t('auth.validation.passwordComplexity')
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: t('auth.validation.passwordMismatch'),
    path: ['confirmPassword'],
  });

type RegisterFormData = {
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
};

type InvitationInfo = {
  email: string;
  tenantId: string;
  tenantName: string;
  tenantDomain: string;
  role: string;
};

export function RegisterPage() {
  const { t } = useTranslation('identity');
  const schema = useMemo(() => createRegisterSchema(t), [t]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setDomain } = useTenant();
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!token) {
      setError(t('auth.register.invalidLink'));
      setIsLoading(false);
      return;
    }

    axios
      .get<InvitationInfo>(`${API_URL}/invitations/verify/${token}`)
      .then((response) => {
        setInvitationInfo(response.data);
        setValue('email', response.data.email);
        setIsLoading(false);
      })
      .catch((err) => {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 404) {
            setError(t('auth.register.missingInvitation'));
          } else if (err.response?.status === 400) {
            setError(t('auth.register.expiredInvitation'));
          } else {
            setError(t('auth.register.verifyFailed'));
          }
        } else {
          setError(t('auth.register.verifyFailed'));
        }
        setIsLoading(false);
      });
  }, [setValue, t, token]);

  const onSubmit = async (data: RegisterFormData) => {
    if (!token || !invitationInfo) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/invitations/accept/${token}`, {
        email: data.email,
        name: data.name,
        password: data.password,
      });

      setDomain(invitationInfo.tenantDomain);

      await login({
        email: data.email,
        password: data.password,
        domain: invitationInfo.tenantDomain,
      });

      navigate('/', { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 400) {
          setError(t('auth.register.acceptFailed'));
        } else if (err.response?.status === 409) {
          setError(t('auth.register.userExists'));
        } else {
          setError(t('auth.register.acceptFailed'));
        }
      } else {
        setError(t('auth.register.acceptFailed'));
      }
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page text-content-primary">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="text-sm text-content-secondary">{t('auth.register.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !invitationInfo) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
        <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 text-center shadow-surface">
          <h1 className="mb-4 text-2xl font-semibold text-content-primary">
            {t('auth.register.invalidTitle')}
          </h1>
          <p className="mb-6 text-content-secondary">{error}</p>
          <Button onClick={() => navigate('/login')} className="w-full">
            {t('auth.register.goToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
      <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 shadow-surface">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-content-primary">
            {t('auth.register.title')}
          </h1>
          <p className="mt-2 text-sm text-content-muted">
            {t('auth.register.subtitle', {
              tenant: invitationInfo?.tenantName ?? t('auth.register.defaultTenant'),
            })}
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {t('auth.common.email')}
            </label>
            <Input type="email" disabled {...register('email')} className="bg-surface-secondary" />
            {errors.email && (
              <p className="mt-1 text-xs text-state-danger">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {t('auth.register.name')}
            </label>
            <Input placeholder={t('auth.register.namePlaceholder')} {...register('name')} />
            {errors.name && (
              <p className="mt-1 text-xs text-state-danger">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {t('auth.common.password')}
            </label>
            <Input type="password" {...register('password')} />
            {errors.password && (
              <p className="mt-1 text-xs text-state-danger">{errors.password.message}</p>
            )}
            <p className="mt-1 text-xs text-content-muted">
              {t('auth.register.passwordHint')}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {t('auth.common.confirmPassword')}
            </label>
            <Input type="password" {...register('confirmPassword')} />
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-state-danger">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {error && <p className="text-sm text-state-danger">{error}</p>}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('auth.register.creating') : t('auth.register.create')}
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-content-muted">
          {t('auth.register.hasAccount')}{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-content-accent hover:text-primary"
          >
            {t('auth.register.signIn')}
          </button>
        </p>
      </div>
    </div>
  );
}
